import React, { useContext, useMemo, useState } from 'react';
import EDCaptionedValue from '../../../../../../../BaseComponents/CaptionedValue/EDCaptionedValue';
import EDColumns, {
  EDColumn,
} from '../../../../../../../BaseComponents/Columns/EDColumns';
import EDBox from '../../../../../../../BaseComponents/EDBox/EDBox';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';
import { selectedId } from '../../../../../../../Components/MasterDetail/MasterDetailUtils';
import {
  TEDEKBokforderTYPEnum,
  TEDSYSUtbetalningssattBETSATTEnum,
} from '../../../../../../../globalQueryTypes';
import {
  IBusinessCollectionProps,
  IObjektVy,
  IObjektVyComp,
} from '../../../../../../../Types/ObjektVy';
import Attribut from '../../../../../_Common/Components/BusinessCollectionComponents/Attribut/Attribut';
import { useBusinessDataFormContext } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import { FormUpdateAssociatonType } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormValueUtils';
import { ObjektLank } from '../../../../../_Common/Components/BusinessCollectionComponents/ObjektLank/ObjektLank';
import KonteringContext from '../../../../../_Common/Components/KonteringManager/KonteringContext';
import {
  UtanordningQuery_node_TEDEKBokforder,
  UtanordningQuery_node_TEDEKBokforder__Associations,
} from '../../Queries/_QueryTypes/UtanordningQuery';
import {
  filterUtbetalningssatt,
  validBankkontoUtbetalningsatt,
  validMessageUtbetalningsatt,
  validPGBGUtbetalingsatt,
} from '../../Utils/UtanordningUtils';
import AttestantPicker from '../AttestantPicker/AttestantPicker';
import BokforderContext from '../BokforderContext/BokforderContext';
import { IUserBokforderKodbehorigheter } from '../BokforderContext/BokforderContextManager';

interface IProps extends IBusinessCollectionProps {}

const UtanordningTillgodoFormComp: IObjektVyComp = {
  caption: '',
  comp: (props: IProps) => <UtanordningTillgodoFormView {...props} />,
};

const UtanordningTillgodoFormView: React.FC<IProps> = props => {
  const bokforderContext = useContext(BokforderContext);
  const konteringContext = useContext(KonteringContext);
  const formContext = useBusinessDataFormContext();

  const [valdAttestant, setValdAttestant] =
    useState<null | IUserBokforderKodbehorigheter>(null);

  const kontodelDefFromKodplan = useMemo(() => {
    return konteringContext.konteringUtils.getKontodelDefsByKodplanId(
      bokforderContext.bokforderKodplan?.id ?? ''
    );
  }, [bokforderContext.bokforderKodplan?.id, konteringContext.konteringUtils]);

  const showBankkontoFields = useMemo(() => {
    const utanordningData = formContext?.formState
      .currentValues as UtanordningQuery_node_TEDEKBokforder;
    const betsatt = utanordningData?._Associations?.Utbetalningssatt?.BETSATT;
    return betsatt ? validBankkontoUtbetalningsatt.includes(betsatt) : false;
  }, [formContext?.formState.currentValues]);

  const showPGBGFields = useMemo(() => {
    const utanordningData = formContext?.formState
      .currentValues as UtanordningQuery_node_TEDEKBokforder;
    const betsatt = utanordningData?._Associations?.Utbetalningssatt?.BETSATT;
    return betsatt ? validPGBGUtbetalingsatt.includes(betsatt) : false;
  }, [formContext?.formState.currentValues]);

  const betalningsmottagareColumnWidth = useMemo(() => {
    if (bokforderContext.isEditMode) {
      if (!showBankkontoFields && !showPGBGFields) {
        return 12;
      } else if (showBankkontoFields && !showPGBGFields) {
        return 4;
      } else if (!showBankkontoFields && showPGBGFields) {
        return 8;
      } else {
        return 4;
      }
    } else {
      return 3;
    }
  }, [bokforderContext.isEditMode, showBankkontoFields, showPGBGFields]);

  const numberOfColumns = bokforderContext.isEditMode ? 4 : 3;
  const columnSpacing = bokforderContext.isEditMode ? 1 : 0;
  const bestalldBokfDatumDifferFromBokfDatum =
    formContext?.formState?.currentValues?.BetBokfdatum !==
    formContext?.formState?.currentValues?.BOKF_DATUM;

  return (
    <EDBox
      sx={{
        width: theme => `calc(100% - ${theme.spacing(2)})`,
        height: 'auto',
        paddingX: theme => theme.spacing(1),
      }}
    >
      <EDColumns spacing={columnSpacing}>
        <EDColumn md={numberOfColumns} pt={0}>
          <EDBox sx={{ flex: 1 }}>
            {bestalldBokfDatumDifferFromBokfDatum &&
            !bokforderContext.isEditMode ? (
              <EDCaptionedValue
                label="Beställningsdatum"
                name="bestalltBokfdatum"
                size="large"
              >
                <EDTypography display="inline-block">{` ${formContext?.formState?.currentValues?.BetBokfdatum} (bokförd: ${formContext?.formState?.currentValues?.BOKF_DATUM})`}</EDTypography>
              </EDCaptionedValue>
            ) : (
              <Attribut
                disableEditMode={!bokforderContext.isEditMode}
                name="BOKF_DATUM"
                customDisplayLabel="Beställningsdatum"
                disabled
                size="large"
              />
            )}
          </EDBox>
        </EDColumn>
        <EDColumn md={numberOfColumns} pt={0}>
          <EDBox sx={{ flex: 1 }}>
            <Attribut
              disableEditMode={!bokforderContext.isEditMode}
              name="U_BELOPP"
              size="large"
            />
          </EDBox>
        </EDColumn>

        {bokforderContext.tillgodohavandeKundTyp && (
          //Fullösning för att validValuesFunction körs endast när den renderas första gången och då har man
          // inte hunnit att sätta ett nytt värde på bokforderContext.tillgodohavandeKundTyp
          <EDColumn md={numberOfColumns} pt={0}>
            <EDBox sx={{ flex: 1 }}>
              <ObjektLank<UtanordningQuery_node_TEDEKBokforder__Associations>
                associationName="Utbetalningssatt"
                editMode={bokforderContext.isEditMode}
                disableAssociationLink
                id={selectedId(props.id) ?? 'Utbetalningssatt'}
                size="ownblock"
                fullWidth
                validValues={{
                  dataDependencies: [
                    'BETSATT',
                    'BORTTAGEN',
                    'Utbetalningsvag.AKTIV',
                    'Utbetalningsvag.BORTTAGEN',
                  ],

                  validValuesFunction: defaultValues => {
                    const filteredBetalningsatt = filterUtbetalningssatt(
                      bokforderContext.bokforderVariant,
                      defaultValues,
                      bokforderContext.tillgodohavandeKundTyp
                    );

                    if (
                      filteredBetalningsatt.length === 1 &&
                      formContext?.formState.currentValues?._Associations
                        ?.Utbetalningssatt == null
                    ) {
                      const updateValue: FormUpdateAssociatonType[] = [
                        {
                          operation: 'Ref',
                          associationName: 'Utbetalningssatt',
                          id: filteredBetalningsatt[0].value,
                          values: {
                            id: filteredBetalningsatt[0].value,
                            BETSATT: filteredBetalningsatt[0].data.BETSATT,
                          },
                        },
                      ];
                      formContext?.update(updateValue);
                    }
                    return filteredBetalningsatt;
                  },
                }}
              />
            </EDBox>
          </EDColumn>
        )}
      </EDColumns>
      <EDColumns spacing={columnSpacing}>
        <EDColumn md={betalningsmottagareColumnWidth} pt={0}>
          <EDBox sx={{ flex: 1 }}>
            <Attribut
              disableEditMode={!bokforderContext.isEditMode}
              name="U_BETALNINGSMOTTAGARE"
              size="large"
            />
          </EDBox>
        </EDColumn>
        {showBankkontoFields && (
          <>
            <EDColumn md={numberOfColumns} pt={0}>
              <EDBox sx={{ flex: 1 }}>
                <Attribut
                  disableEditMode={!bokforderContext.isEditMode}
                  name="U_BANK"
                  size="large"
                  isRequired
                />
              </EDBox>
            </EDColumn>
            <EDColumn md={numberOfColumns} pt={0}>
              <EDBox sx={{ flex: 1 }}>
                <Attribut
                  disableEditMode={!bokforderContext.isEditMode}
                  name="U_CLEARING_BANKKONTONUMMER"
                  size="large"
                />
              </EDBox>
            </EDColumn>
          </>
        )}

        {showPGBGFields && (
          <EDColumn
            md={bokforderContext.isEditMode ? 4 : numberOfColumns}
            pt={0}
          >
            <EDBox sx={{ flex: 1 }}>
              <Attribut
                disableEditMode={!bokforderContext.isEditMode}
                name="U_BANK_PLUSGIRONUMMER"
                size="large"
              />
            </EDBox>
          </EDColumn>
        )}
      </EDColumns>
      <EDColumns spacing={columnSpacing}>
        {validMessageUtbetalningsatt.includes(
          formContext?.formState.currentValues?._Associations?.Utbetalningssatt
            ?.BETSATT
        ) ? (
          <EDColumn md={8} pt={0}>
            <EDBox sx={{ flex: 2 }}>
              <Attribut
                customDisplayLabel="Meddelande till mottagare"
                disableEditMode={!bokforderContext.isEditMode}
                name="U_MEDDELANDE"
                size="large"
                maxInputLength={
                  formContext?.formState.currentValues?._Associations
                    ?.Utbetalningssatt?.BETSATT ===
                  TEDSYSUtbetalningssattBETSATTEnum.KONTOINSATTNING
                    ? 12
                    : undefined
                }
              />
            </EDBox>
          </EDColumn>
        ) : (
          <EDColumn md={8} pt={0}>
            <EDBox sx={{ flex: 2 }}>
              <Attribut
                customDisplayLabel="Rubrik"
                disableEditMode={!bokforderContext.isEditMode}
                name="TEXT"
                size="large"
                isRequired
              />
            </EDBox>
          </EDColumn>
        )}

        {bokforderContext.isEditMode && (
          <EDColumn md={4} pt={0}>
            <EDBox sx={{ flex: 1 }}>
              <AttestantPicker
                isDisabled={false}
                kontodelDefFromKodplan={kontodelDefFromKodplan}
                setValdAttestant={setValdAttestant}
                type={TEDEKBokforderTYPEnum.UTANORDNING}
                valdAttestant={valdAttestant}
              />
            </EDBox>
          </EDColumn>
        )}
      </EDColumns>
      {validMessageUtbetalningsatt.includes(
        formContext?.formState.currentValues?._Associations?.Utbetalningssatt
          ?.BETSATT
      ) && (
        <EDColumns spacing={columnSpacing}>
          <EDColumn md={8} pt={0}>
            <EDBox sx={{ flex: 2 }}>
              <Attribut
                customDisplayLabel="Rubrik"
                disableEditMode={!bokforderContext.isEditMode}
                name="TEXT"
                size="large"
                isRequired
              />
            </EDBox>
          </EDColumn>
        </EDColumns>
      )}
    </EDBox>
  );
};

export default UtanordningTillgodoFormView;

export const UtanordningTillgodoCard: IObjektVy = {
  type: 'ObjektVy',
  id: 'UtanordningTillgodoForm',
  info: {},
  def: () => UtanordningTillgodoFormComp,
};
